import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import './ModalHost.scss';
import { registerModalComponents, useModalStore } from '../../stores/modal-store';
import { ModalType } from '../../interfaces/modal-types.enum';
import type { ModalConfig } from '../../interfaces/modal-config';
import SpinnerLoading from '../components/SpinnerLoading';
import ErrorBoundary from '../components/ErrorBoundary';

// Modals are code-split: each chunk loads only when its modal first opens (none
// are needed for first paint). The Suspense fallback below covers the chunk
// fetch. NOTE(port): Angular eagerly imported all 12 modal components into the
// bundle; lazy() removes that weight from the initial download.
const AffiliationLink = lazy(() => import('./AffiliationLink'));
const DifferentUserPreferences = lazy(() => import('./DifferentUserPreferences'));
const FilterOverload = lazy(() => import('./FilterOverload'));
const LoginRegister = lazy(() => import('./LoginRegister'));
const Lyrics = lazy(() => import('./Lyrics'));
const PurchaseSuccess = lazy(() => import('./PurchaseSuccess'));
const ReportIssue = lazy(() => import('./ReportIssue'));
const SongMetadata = lazy(() => import('./SongMetadata'));
const YouTubeSuggestions = lazy(() => import('./YouTubeSuggestions'));
const YoutubeUrlWindow = lazy(() => import('./YoutubeUrlWindow'));

// Port of src/app/shared/modal/modal-container/modal-container.component.ts +
// .html. Renders the active modal stack with the same backdrop and
// scale/fade animations as the Angular @trigger definitions (see
// ModalHost.scss). Per-modal Escape handling lives in each modal component
// (mirroring BaseModalComponent's HostListener) — see use-base-modal.ts.
//
// Backdrop clicks intentionally do NOT close modals: the Angular backdrop div
// had no click handler and Flowbite was initialized with closable:false, so
// clicking the backdrop did nothing. Replicated by rendering the backdrop
// without an onClick.

// Mirrors Angular ModalService's ModalType → component map (modal.service.ts).
registerModalComponents([
  [ModalType.AFFILIATION_LINK, AffiliationLink],
  [ModalType.FILTER_OVERLOAD, FilterOverload],
  [ModalType.LOGIN_REGISTER, LoginRegister],
  [ModalType.LYRICS, Lyrics],
  [ModalType.PURCHASE_SUCCESS, PurchaseSuccess],
  [ModalType.REPORT_ISSUE, ReportIssue],
  [ModalType.SONG_METADATA, SongMetadata],
  [ModalType.YOUTUBE_SUGGESTIONS, YouTubeSuggestions],
  [ModalType.YOUTUBE_URL_WINDOW, YoutubeUrlWindow],
  [ModalType.DIFFERENT_USER_PREFERENCES, DifferentUserPreferences],
]);

// Angular's :leave transitions ran for 150ms; entries are kept mounted with the
// leave class for that long before being dropped.
const LEAVE_ANIMATION_MS = 150;

interface RenderedModal {
  config: ModalConfig;
  leaving: boolean;
}

// Angular: getModalZIndex($index) = 1002 + index * 10.
function getModalZIndex(index: number): number {
  return 1002 + (index * 10);
}

// Angular: backdrop sits just under the topmost modal (1001 when none).
function getBackdropZIndex(modalCount: number): number {
  return modalCount > 0 ? getModalZIndex(modalCount - 1) - 1 : 1001;
}

export default function ModalHost() {
  const activeModals = useModalStore(s => s.activeModals);
  const isLoading = useModalStore(s => s.isLoading);

  // Locally rendered stack: active modals plus modals playing their leave
  // animation (Angular's animation engine kept :leave elements in the DOM).
  const [rendered, setRendered] = useState<RenderedModal[]>([]);
  const leaveTimersRef = useRef(new Map<string, number>());

  useEffect(() => {
    setRendered(prev => {
      const activeById = new Map(activeModals.map(m => [m.id, m]));
      const next: RenderedModal[] = [];

      for (const entry of prev) {
        const active = activeById.get(entry.config.id);
        if (active) {
          next.push({ config: active, leaving: false });
        } else {
          next.push(entry.leaving ? entry : { ...entry, leaving: true });
          if (!leaveTimersRef.current.has(entry.config.id)) {
            const id = entry.config.id;
            const timer = window.setTimeout(() => {
              leaveTimersRef.current.delete(id);
              setRendered(curr => curr.filter(e => e.config.id !== id));
            }, LEAVE_ANIMATION_MS);
            leaveTimersRef.current.set(id, timer);
          }
        }
      }

      for (const modal of activeModals) {
        if (!next.some(e => e.config.id === modal.id)) {
          next.push({ config: modal, leaving: false });
        }
      }

      return next;
    });
  }, [activeModals]);

  useEffect(() => {
    const timers = leaveTimersRef.current;
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const hasModals = activeModals.length > 0;
  const showBackdrop = rendered.length > 0;
  const backdropLeaving = !hasModals;

  return (
    <div className="modal-container transition-all">
      {/* Backdrop */}
      {showBackdrop && (
        <div
          className={`modal-backdrop ${backdropLeaving ? 'backdrop-anim-leave' : 'backdrop-anim-enter'}`}
          style={{ zIndex: getBackdropZIndex(activeModals.length) }}>
        </div>
      )}

      {/* Modals */}
      {rendered.map((entry, index) => {
        const { config, leaving } = entry;
        const ModalComponent = config.component;
        return (
          <div key={config.id}
               className={`modal-wrapper ${leaving ? 'modal-anim-leave' : 'modal-anim-enter'}`}
               style={{ zIndex: getModalZIndex(index) }}>
            <div className="modal-content-wrapper">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-50">
                  <SpinnerLoading/>
                </div>
              )}
              <div className={isLoading ? 'blur-sm' : ''}>
                <ErrorBoundary
                    name={`modal:${config.id}`}
                    fallback={
                      <div className="flex flex-col items-center justify-center gap-4 p-8 text-center text-gray-700 dark:text-gray-300">
                        <p className="text-base font-medium">Something went wrong opening this dialog.</p>
                        <button
                            onClick={() => useModalStore.getState().close(config.id)}
                            className="px-4 py-2 rounded-lg bg-mypart_red-500 text-white hover:bg-mypart_red-600 transition-colors">
                          Close
                        </button>
                      </div>
                    }>
                  <Suspense fallback={<div className="flex items-center justify-center p-10"><SpinnerLoading/></div>}>
                    <ModalComponent
                      {...config.inputs}
                      modalId={config.id}
                      close={() => useModalStore.getState().close(config.id)}/>
                  </Suspense>
                </ErrorBoundary>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
